import { useState } from 'react';
import { Card } from './Card';
import { Button } from './Button';
import { Select } from './Select';
import { FileDropzone } from './FileDropzone';
import { computeHash } from '../services/hashService';
import { HashAlgorithm, EncodingFormat, HashResult } from '../types';
import { translations } from '../i18n/hu';
import { Download, Copy, Check } from 'lucide-react';

const algorithms: { value: HashAlgorithm; label: string }[] = [
  { value: 'MD5', label: 'MD5' },
  { value: 'SHA-1', label: 'SHA-1' },
  { value: 'SHA-256', label: 'SHA-256' },
  { value: 'SHA-384', label: 'SHA-384' },
  { value: 'SHA-512', label: 'SHA-512' },
  { value: 'SHA3-256', label: 'SHA3-256' },
  { value: 'BLAKE2b', label: 'BLAKE2b' },
  { value: 'CRC32', label: 'CRC32' },
];

const encodings: { value: EncodingFormat; label: string }[] = [
  { value: 'hex', label: translations.encoding.hex },
  { value: 'hex-upper', label: translations.encoding.hexUpper },
  { value: 'base64', label: translations.encoding.base64 },
];

export function BatchHash() {
  const [files, setFiles] = useState<File[]>([]);
  const [selectedAlgorithms, setSelectedAlgorithms] = useState<HashAlgorithm[]>(['SHA-256']);
  const [encoding, setEncoding] = useState<EncodingFormat>('hex');
  const [results, setResults] = useState<HashResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [copiedIndex, setCopiedIndex] = useState<string | null>(null);
  const [error, setError] = useState<string>('');

  const handleFilesSelected = (selectedFiles: File[]) => {
    setFiles(selectedFiles);
    setResults([]);
  };

  const toggleAlgorithm = (algo: HashAlgorithm) => {
    setSelectedAlgorithms(prev =>
      prev.includes(algo)
        ? prev.filter(a => a !== algo)
        : [...prev, algo]
    );
  };

  const handleGenerate = async () => {
    if (files.length === 0) {
      setError('Kérjük, válasszon ki legalább egy fájlt');
      return;
    }
    if (selectedAlgorithms.length === 0) {
      setError('Kérjük, válasszon ki legalább egy algoritmust');
      return;
    }

    setIsLoading(true);
    setError('');
    setResults([]);
    setProgress(0);

    try {
      const newResults: HashResult[] = [];
      const total = files.length * selectedAlgorithms.length;
      let processed = 0;

      for (const file of files) {
        for (const algorithm of selectedAlgorithms) {
          const result = await computeHash(file, algorithm, encoding);
          newResults.push(result);
          processed++;
          setProgress((processed / total) * 100);
          setResults([...newResults]);
        }
      }

      setProgress(100);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ismeretlen hiba történt');
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  };

  const handleCopy = (index: string, hash: string) => {
    navigator.clipboard.writeText(hash);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleExportCSV = () => {
    const headers = ['Fájlnév', 'Algoritmus', 'Kódolás', 'Hash', 'Méret (bájt)', 'Időtartam (ms)'];
    const rows = results.map(r => [
      r.fileName || 'N/A',
      r.algorithm,
      r.encoding,
      r.hash,
      r.inputLength.toString(),
      r.durationMs.toFixed(2),
    ]);

    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hashlab-batch-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadChecksums = () => {
    const grouped = results.reduce((acc, result) => {
      if (!acc[result.algorithm]) {
        acc[result.algorithm] = [];
      }
      acc[result.algorithm].push(result);
      return acc;
    }, {} as Record<string, HashResult[]>);

    Object.entries(grouped).forEach(([algo, algoResults]) => {
      const extension = algo.toLowerCase().replace('/', '-');
      const content = algoResults
        .map(r => `${r.hash}  ${r.fileName || 'file'}\n`)
        .join('');
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `checksums.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div className="space-y-6">
      <Card title={translations.batch.title}>
        <div className="space-y-4">
          <FileDropzone
            onFilesSelected={handleFilesSelected}
            multiple={true}
            maxSize={4096}
          />

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              Algoritmusok (több választható)
            </label>
            <div className="flex flex-wrap gap-2">
              {algorithms.map(algo => (
                <button
                  key={algo.value}
                  onClick={() => toggleAlgorithm(algo.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedAlgorithms.includes(algo.value)
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {algo.label}
                </button>
              ))}
            </div>
          </div>

          <Select
            label={translations.encoding.label}
            value={encoding}
            onChange={(e) => setEncoding(e.target.value as EncodingFormat)}
            options={encodings}
          />

          {isLoading && (
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
              <div
                className="bg-primary-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <Button
            onClick={handleGenerate}
            isLoading={isLoading}
            disabled={files.length === 0 || selectedAlgorithms.length === 0}
            className="w-full"
          >
            {translations.common.generate}
          </Button>
        </div>
      </Card>

      {results.length > 0 && (
        <Card title={`Eredmények (${results.length})`}>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleExportCSV} className="flex-1">
                <Download className="w-4 h-4 mr-2" />
                CSV Export
              </Button>
              <Button variant="outline" onClick={handleDownloadChecksums} className="flex-1">
                <Download className="w-4 h-4 mr-2" />
                Checksum Fájlok
              </Button>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {results.map((result, index) => {
                const uniqueKey = `${result.fileName}-${result.algorithm}-${index}`;
                return (
                  <div
                    key={uniqueKey}
                    className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {result.fileName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {result.algorithm} • {(result.inputLength / 1024 / 1024).toFixed(2)} MB • {result.durationMs.toFixed(2)} ms
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopy(uniqueKey, result.hash)}
                      >
                        {copiedIndex === uniqueKey ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                      <p className="font-mono text-sm break-all text-gray-900 dark:text-white">
                        {result.hash}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

